import { useEffect, useRef } from 'react';
import { useNotificationSummary } from './useNotificationSummary';
import {
  buildBadgedTitle,
  clearFaviconBadge,
  flashUnread,
  setFaviconBadge,
  stripTitleBadge,
} from '@/lib/unread-indicator';

/**
 * Surfaces the app-wide unread total beyond the in-page header badge, so it is
 * visible while the user is focused on another tab or window:
 *
 *  - tab title    — `(N) Fias`
 *  - favicon      — red count dot
 *  - flash        — a transient OS/taskbar notification when the total rises
 *                   while focused elsewhere (only if permission already granted)
 *
 * Reads the same `useNotificationSummary` query that feeds the header badge
 * (shared cache, same key), opting into background polling so the count keeps
 * advancing while Fias is hidden. Mount exactly once, app-wide — see the
 * `<UnreadIndicator />` listener used in `App`.
 */
export function useUnreadIndicator(): void {
  // Poll in the background so the total keeps advancing while Fias is hidden —
  // otherwise the flash could never fire while the user is focused elsewhere
  // (the count would only refresh on return, by which point we're focused).
  const { total, isLoading } = useNotificationSummary({ pollInBackground: true });
  // null until the first *settled* total is observed, so a fresh load / reload
  // never flashes for messages that were already unread before this session.
  const prevTotalRef = useRef<number | null>(null);

  // Keep the tab title + favicon in sync with the current total.
  //
  // NOTE: `document.title` has no single owner today (nothing else writes it),
  // so we own the `(N)` prefix. If per-route titles are ever introduced
  // (e.g. react-helmet), this must be reconciled with that owner — re-deriving
  // the base via `stripTitleBadge` keeps us composable, but a title set while
  // `total` is unchanged would not be re-badged until the next count change.
  useEffect(() => {
    document.title = buildBadgedTitle(document.title, total);
    setFaviconBadge(total);
  }, [total]);

  // Flash when the total increases while the user is focused elsewhere.
  // `!document.hasFocus()` covers both a hidden/background tab AND Fias being
  // the active tab while another application window has focus — `document.hidden`
  // alone would miss the latter and never flash in it.
  useEffect(() => {
    // Ignore the pre-load placeholder total (0). Establishing the baseline from
    // the first *settled* value is what prevents a spurious flash on a cold
    // load when there are already-unread messages and the tab isn't focused.
    if (isLoading) return;
    const prev = prevTotalRef.current;
    prevTotalRef.current = total;
    if (prev === null) return; // first settled observation — baseline only
    const focusedElsewhere =
      typeof document !== 'undefined' &&
      typeof document.hasFocus === 'function' &&
      !document.hasFocus();
    if (total > prev && focusedElsewhere) {
      flashUnread(total);
    }
  }, [total, isLoading]);

  // Restore the title + favicon when the indicator unmounts (e.g. logout tears
  // down the tree), so a stale badge doesn't linger.
  useEffect(() => {
    return () => {
      document.title = stripTitleBadge(document.title);
      clearFaviconBadge();
    };
  }, []);
}

/**
 * Renders nothing; drives the cross-tab unread indicator as a side effect.
 * Mount once at the app root, beside the other global listeners.
 */
export function UnreadIndicator(): null {
  useUnreadIndicator();
  return null;
}
